import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import FileLink from "../components/FileLink";
import { Badge, Btn, Card, Spinner, G_DARK, formatWhen } from "../components/Shared";
import { useDisplayTimezone } from "../portalConfig";
import { useAuth } from "../auth/AuthContext";

// What this project's facility asks you to have read.
//
// The half that was missing: everything else about documents is keyed on a
// group and answers "who has read this" for an operator. The subject's own
// question — what does this project require of me — had no route and no screen,
// so a published policy appeared nowhere and could not be confirmed at all.

// **The one sentence, written once.** md5 is not broken for preimages — given a
// digest you cannot construct a file to match it — and it is the check a
// researcher can actually run, against the failure that actually happens:
// truncation and corruption. It is NOT evidence about tampering, and nothing in
// this portal may say that it is. The sha256 goes on being what every
// `Acknowledgement.subject_hash` is compared against.
//
// A second copy of this wording is how three renderers come to disagree about
// what the portal promises — `QuotationSignPage` is already a second renderer of
// a quotation and deliberately carries none of this.
export const CHECK_NOTICE =
  "Check the md5 to confirm the file arrived whole. The sha256 beside it is what the "
  + "signatures are recorded against.";

// Said when there is a sha256 on screen and no md5. The reader still learns what
// the value beside it is for, without being invited to check a file against it.
export const SHA_ONLY_NOTICE =
  "The sha256 is what the signatures are recorded against.";

// Said on every surface, because the other two copies travel outside the portal
// where nothing can say it for them. The 2026-09-08 refusal to append anything
// to a document was right for the reason it gave — a file whose own certificate
// makes its hash wrong is worse than no certificate — and what makes appending
// safe now is exactly this: the original is still there, unchanged, under its
// own name, and it is the one a printed checksum belongs to.
export const ONLY_ORIGINAL_IS_ARCHIVAL =
  "Only the original is the archival copy. The others are re-rendered, so a checksum "
  + "over them is not one this portal has published.";

const MONO = { fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-all" };

/** `?variant=…` onto a path that may already carry a query — `project_id` and
 *  `version` both matter, and dropping either serves a different document. */
function withVariant(path, variant) {
  return `${path}${path.includes("?") ? "&" : "?"}variant=${variant}`;
}

/**
 * The files one signed artefact can be downloaded as, and which of them a
 * checksum belongs to.
 *
 * **It lives here, in the smallest of the four surfaces that need it, rather
 * than in `components/`** — that directory is the design system and is shared
 * with work running beside this change; a primitive added there is a change to
 * every screen. What must not happen is the wording existing four times, which
 * is the failure this whole feature exists to prevent, arriving through the fix.
 *
 * `md5` and `sha256` are **the values the API returned**. Hashing the blob the
 * browser already holds would say nothing about the file the person later saves
 * — it would be a check of the download against itself.
 *
 * An absent md5 is an absence, never a zero and never a substitute value: a
 * Markdown version is rendered at download and a quotation sent before the
 * freeze existed has no stored file at all. `absent` is the caller's sentence
 * for its own case, and only a caller that HOLDS the field may pass one — a
 * screen whose payload simply does not carry the md5 says nothing about it.
 */
export function ArchivalCopy({ path, md5 = null, sha256 = null, absent = null,
                              merged = true, defaultLabel = "PDF", align = "flex-start" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: align, gap: 4 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
        {/* First, because it is the one to archive. */}
        <FileLink path={withVariant(path, "original")}>Original</FileLink>
        {/* What this link has always given, at the address it has always had:
            no variant means the route behaves exactly as it did. */}
        <FileLink path={path}>{defaultLabel}</FileLink>
        {/* A different file under a different name. Same name is how somebody
            archives the merged copy and fails the md5 on it a year later. */}
        {merged && (
          <FileLink path={withVariant(path, "merged")}>With signatures</FileLink>
        )}
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", textAlign: align === "flex-end" ? "right" : "left" }}>
        {/* Nothing at all when the payload carried neither value — an empty line
            saying nothing is how a screen comes to look as though a checksum
            failed to load. */}
        {(md5 || absent || sha256) && (
          <div>
            {md5 ? <>Original md5 <code style={MONO}>{md5}</code></> : (absent || null)}
            {sha256 && (
              <>{(md5 || absent) ? " · " : ""}sha256 <code style={MONO}>{sha256}</code></>
            )}
          </div>
        )}
        {(md5 || sha256) && <div>{md5 ? CHECK_NOTICE : SHA_ONLY_NOTICE}</div>}
        <div>{ONLY_ORIGINAL_IS_ARCHIVAL}</div>
      </div>
    </div>
  );
}


export default function ProjectDocumentsCard({ project, onUpdated }) {
  // The display zone, like every other timestamp in the app. Printed raw,
  // these read "2026-08-23T09:14:02.481773+00:00" — UTC, to the microsecond,
  // and two hours out for a reader in Vienna.
  const zone = useDisplayTimezone();
  // The banner and the Projects badge read the shared count, not this card's
  // list: confirming here has to refresh both, or the banner keeps asking.
  const { refreshDuties } = useAuth();
  const [docs, setDocs]   = useState(null);
  const [open, setOpen]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  const load = () => api.get(`/api/projects/${project.id}/documents`)
    .then(setDocs).catch(e => setError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDocs(null); load(); }, [project.id]);

  const confirm = async doc => {
    setBusy(true); setError(null);
    try {
      const scope = doc.project_id ? `?project_id=${doc.project_id}` : "";
      await api.post(`/api/groups/${doc.group_id}/documents/${doc.id}/acknowledge${scope}`);
      await load();
      refreshDuties?.();
      // The confirmation may be what the transfer gate was waiting for, and
      // `can_transfer_data` is the server's answer on the project itself. Hand
      // the page the refetched project rather than keeping a second copy of the
      // gate here: the Submissions and Results tabs open without a reload.
      if (onUpdated) onUpdated(await api.get(`/api/projects/${project.id}`));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (docs === null || docs.length === 0) return null;

  const outstanding = docs.filter(d => d.mode === "acknowledge" && !d.acknowledged);

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Documents</h3>
        {outstanding.length > 0 && (
          <Badge color="amber">{outstanding.length} awaiting your confirmation</Badge>
        )}
        <span style={{ flex: 1 }} />
        {/* Every document this project has, in one file: the policies it is
            subject to and the quotations raised against it. mode="save" because
            a .tar.gz is not something a browser renders, and FileLink rather
            than an anchor because a navigation carries no Authorization header
            — see the note in api.js.

            Hidden when there is nothing: the endpoint refuses an empty archive
            rather than sending a container with nothing in it, and a button
            whose only outcome is an error is worse than no button. */}
        {docs.length > 0 && (
          <FileLink path={`/api/projects/${project.id}/documents/archive`}
                    mode="save">Download all</FileLink>
        )}
      </div>

      {error && (
        <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {docs.map(d => (
          <div key={d.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <strong style={{ fontSize: 14 }}>{d.title}</strong>
              <span style={{ color: "#6b7280", fontSize: 12 }}>v{d.version}</span>
              {d.mode === "acknowledge" && (
                d.acknowledged
                  ? <Badge color="green">Confirmed</Badge>
                  : <Badge color="amber">Please confirm</Badge>
              )}
              <span style={{ flex: 1 }} />
              <button onClick={() => setOpen(open === d.id ? null : d.id)}
                      style={{ background: "none", border: "none", cursor: "pointer",
                               color: "#374151", fontSize: 13, fontFamily: "inherit" }}>
                {open === d.id ? "Hide" : "Read"}
              </button>
            </div>

            {/* project_id, or a per-project document's PDF cannot know which
                confirmation is this reader's — and answering from a sibling
                project printed a certificate on a project they never signed. The
                variant is appended to that query rather than replacing it.

                No md5 is shown: `/api/projects/{id}/documents` does not carry
                one. `content_hash` is the value `subject_hash` is written from,
                so it is the sha256 the signatures really are recorded against —
                and saying nothing about an md5 is the honest answer here,
                because this payload cannot tell "there is none" from "it was
                not sent". */}
            <div style={{ marginTop: 8 }}>
              <ArchivalCopy
                path={`/api/groups/${d.group_id}/documents/${d.id}/pdf?project_id=${project.id}`}
                sha256={d.content_hash} />
            </div>

            {open === d.id && (
              <div data-html style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f3f4f6", fontSize: 14 }}
                   dangerouslySetInnerHTML={{ __html: d.html }} />
            )}

            {d.mode === "acknowledge" && !d.acknowledged && d.rule === "per_project" && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f3f4f6" }}>
                {/* The exact words being agreed to, shown before the button and
                    stored with the confirmation — a record that somebody
                    confirmed, without saying what, is not evidence of anything. */}
                <p style={{ margin: "0 0 8px", fontSize: 13, color: "#374151" }}>{d.intent_text}</p>
                <Btn onClick={() => confirm(d)} disabled={busy}>
                  {busy ? <><Spinner /> Recording…</> : "Accept the document"}
                </Btn>
              </div>
            )}

            {/* By the READER's rule (`rule`, 2026-09-29), never the document's
                `scope`: one policy is `once` for an operator and `per_project`
                for a researcher.
                A facility-wide policy is ONE confirmation for the person, not
                one per project, so confirming it from inside whichever project
                they happened to open made the obligation look like a property of
                that project. It is confirmed in Personal Administration since
                2026-09-08; this stays read-only and says where to go. The
                project's archive still includes it — an archive that omitted a
                policy the project is subject to would be wrong. */}
            {d.mode === "acknowledge" && !d.acknowledged && d.rule !== "per_project" && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f3f4f6",
                            fontSize: 13, color: "#374151" }}>
                This one applies to the whole facility rather than to this project.{" "}
                <Link to="/profile" style={{ color: G_DARK }}>Confirm it in Personal Administration</Link>.
              </div>
            )}

            {d.acknowledged && (
              <div style={{ marginTop: 6, fontSize: 12, color: G_DARK }}>
                Confirmed {formatWhen(d.acknowledged_at, { zone })}. Recorded with the date, your address and a
                fingerprint of this version.
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
